import type GlobalModel from 'echarts/types/src/model/Global';
import type ExtensionAPI from 'echarts/types/src/core/ExtensionAPI';
import { graphic, util } from 'echarts/core';
import { type EChartsExtensionInstallRegisters } from 'echarts';

import type AxisModel from 'echarts/types/src/coord/cartesian/AxisModel';
import type CartesianAxisView from 'echarts/types/src/component/axis/CartesianAxisView';
import type { Group, Text } from 'zrender';
import { isNumber, isFunction } from 'zrender/lib/core/util';
import { AxisBaseOptionCommon } from 'echarts/types/src/coord/axisCommonTypes';

const TWO = 2;

function customizeXAxisSplitLine(ecModel, api) {
  ecModel.eachComponent('xAxis', compModel => {
    const ticks = compModel.axis.scale.getTicks();
    const { option } = compModel;
    const compView = api.getViewOfComponentModel(compModel);
    const lines = [];
    // @ts-ignore
    (compView._axisGroup as Group).traverse(childEl => {
      if ((childEl.anid || '').startsWith('line_')) {
        lines.push(childEl as Text);
      }
    });

    // 自定义分割线样式
    if (isFunction(option?.splitLine?.lineStyle)) {
      lines.forEach((line, index) => {
        const { anid = '' } = line;
        const label = anid.split('_')[1];
        const lineStyle = { ...line.style };
        const customLineStyle = option.splitLine.lineStyle(index, label);
        util.merge(lineStyle, customLineStyle, true);
        line.attr({ style: lineStyle });
      });
    }

    // IOS16分割线样式异常补丁
    // TODO:缺少设备判断逻辑
    lines.forEach(line => {
      const { lineWidth } = line.style;
      line.attr({ style: { lineWidth: lineWidth * (1 + 0.1 * (0.5 + 0.5 * Math.random())) } });
    });
  });
}

/**
 * 处理x轴首尾坐标标签的位置
 */
function parseXAxisLabel(ecModel: GlobalModel, api: ExtensionAPI) {
  // eslint-disable-next-line complexity, sonarjs/cognitive-complexity
  ecModel.eachComponent('xAxis', (compModel: AxisModel) => {
    // @ts-ignore
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (!compModel?.get('show') || !compModel?.get('axisLabel')?.dvAlignEdge) {
      return;
    }

    const compView = api.getViewOfComponentModel(compModel) as CartesianAxisView;
    const labels: Text[] = [];

    // @ts-ignore
    (compView._axisGroup as Group).traverse(childEl => {
      if ((childEl.anid || '').startsWith('label')) {
        labels.push(childEl as Text);
      }
    });

    if (labels.length < TWO) {
      return;
    }

    // * 两端标签对齐（当标签超出轴范围后，缩到轴范围内）
    const firstLabel = labels[0];
    const lastLabel = labels[labels.length - 1];
    const extentCoord = [
      compModel.axis.toGlobalCoord(compModel.axis.getExtent()[0]),
      compModel.axis.toGlobalCoord(compModel.axis.getExtent()[1])
    ];

    if (firstLabel.x + firstLabel.getBoundingRect().x < extentCoord[0]) {
      firstLabel.stopAnimation(null, true);
      firstLabel.attr({
        x: extentCoord[0],
        style: {
          align: 'left'
        }
      });
    }
    if (
      lastLabel.x + lastLabel.getBoundingRect().x + lastLabel.getBoundingRect().width >
      extentCoord[1]
    ) {
      lastLabel.stopAnimation(null, true);
      lastLabel.attr({
        x: extentCoord[1],
        style: {
          align: 'right'
        }
      });
    }

    // * 额外的逻辑
    // 解决标签重叠

    // TODO: echarts也有自己的隐藏逻辑，需要增加这部分的判断，即极值有可能是不显示的
    const secondChild = labels[1];
    const secondLastChild = labels[labels.length - TWO];
    const secondChildSpan = secondChild._children.find(d => d.type === 'tspan');
    const secondLastChildSpan = secondLastChild._children.find(d => d.type === 'tspan');

    const firstChildRect = firstLabel.getBoundingRect();
    const secondChildRect = secondChild.getBoundingRect();
    const secondLastChildRect = secondLastChild.getBoundingRect();
    const lastChildRect = lastLabel.getBoundingRect();

    const headClip = graphic.clipRectByRect(
      {
        x: firstLabel.x + firstChildRect.x,
        y: firstLabel.y + firstChildRect.y,
        width: firstChildRect.width,
        height: firstChildRect.height
      },
      {
        x: secondChild.x + secondChildRect.x,
        y: secondChild.y + secondChildRect.y,
        width: secondChildRect.width,
        height: secondChildRect.height
      }
    );

    const tailClip = graphic.clipRectByRect(
      {
        x: lastLabel.x + lastChildRect.x,
        y: lastLabel.y + lastChildRect.y,
        width: lastChildRect.width,
        height: lastChildRect.height
      },
      {
        x: secondLastChild.x + secondLastChildRect.x,
        y: secondLastChild.y + secondLastChildRect.y,
        width: secondLastChildRect.width,
        height: secondLastChildRect.height
      }
    );

    if (headClip) {
      secondChildSpan?.attr({
        style: { text: '' }
      });
    }

    if (tailClip) {
      secondLastChildSpan?.attr({
        style: { text: '' }
      });
    }
  });
}
// /**
//  * x轴名称位置定位
//  * @description dvAlignEdge: 与x轴标签的距离，按照x轴标签中最大高度计算
//  * @param chart
//  */
//  function parseXAxisNameAlign(chart: ExtensionAPI) {
//   // 获取全部的y轴Model
//   const xAxisModels = chart.getModel().findComponents({
//     mainType: 'xAxis'
//   });
//   if (!xAxisModels.length) {
//     return;
//   }
//   xAxisModels.forEach(xAxisModel => {
//     const dvAlignEdge = xAxisModel.get(['nameTextStyle', 'dvAlignEdge']) as number;
//     const nameGap = xAxisModel.get('nameGap') as number;
//     const axisLabel = xAxisModel.get('axisLabel') as AxisBaseOptionCommon['axisLabel'];
//     const view = chart.getViewOfComponentModel(xAxisModel);
//     let labelHeight = 0;
//     const labelMargin = axisLabel.show ? axisLabel.margin : 0;
//     (view._axisGroup as Group).traverse(el => {
//       if (el.anid?.indexOf('label_') === 0) {
//         labelHeight = Math.max(el.getBoundingRect().height, labelHeight);
//       }
//       if (el.anid === 'name' && isNumber(dvAlignEdge)) {
//         el.attr({
//           y: el.y - nameGap + labelMargin + labelHeight + dvAlignEdge
//         });
//       }
//     });
//   });
// }

export function install(registers: EChartsExtensionInstallRegisters) {
  registers.registerUpdateLifecycle('afterupdate', (ecModel, api) => {
    // @ts-ignore
    parseXAxisLabel(ecModel, api);
    // parseXAxisNameAlign(api);
    customizeXAxisSplitLine(ecModel, api);
  });
}
